import axios from 'axios'

/**
 * Cliente OAuth2/OIDC (Authorization Code + PKCE) contra o auth server.
 *
 * O access_token JWT fica só em memória (nunca em localStorage/sessionStorage
 * — um XSRF/XSS que lesse storage não deveria conseguir levá-lo embora). O
 * refresh_token nem chega a existir aqui: é um cookie httpOnly que o próprio
 * auth server seta e lê, o browser cuida do resto.
 */

// redirect_uri é obrigatório pro OAuth mesmo quando a navegação nunca
// acontece de fato (o login aqui é via XHR — ver LoginJSON no auth server):
// tem que bater exatamente com o que foi cadastrado no client.
export const REDIRECT_URI = window.location.origin + '/oauth/callback'
// Client público do realm `user` — estático no próprio auth server (nenhuma
// linha em auth.clients), client_id "user" por padrão.
export const CLIENT_ID = 'user'
// Client público do realm `admin` — mesma ideia, client_id "admin" fixo no
// auth server. É o realm, não o client_id, que decide contra qual tabela
// (public.users ou public.admins) o login autentica.
export const ADMIN_CLIENT_ID = 'admin'

const authAxios = axios.create({
    baseURL: window.env.AUTH_SERVICE_URL,
    withCredentials: true,
})

type TokenState = {
    accessToken: string | null
    expiresAt: number | null
    // client_id da sessão ativa (user ou admin) — refreshAccessToken/
    // revokeSession precisam dele para acertar com qual client conversar,
    // sem que cada chamador tenha que lembrar disso a cada request.
    clientId: string | null
}

const tokenState: TokenState = {
    accessToken: null,
    expiresAt: null,
    clientId: null,
}

// Guarda o code_verifier entre a chamada de login e a troca por token — tudo
// dentro da mesma navegação de página, nunca precisa sobreviver a um reload.
let pendingCodeVerifier: string | null = null

export function getAccessToken(): string | null {
    return tokenState.accessToken
}

export function setAccessToken(
    token: string,
    expiresInSeconds: number,
    clientId: string
) {
    tokenState.accessToken = token
    tokenState.expiresAt = Date.now() + expiresInSeconds * 1000
    tokenState.clientId = clientId
}

export function clearAccessToken() {
    tokenState.accessToken = null
    tokenState.expiresAt = null
    tokenState.clientId = null
}

/**
 * Em que regime a sessão atual está autenticando:
 *
 * - `jwt`: o caso normal — access token do pcare-auth, em memória, anexado no
 *   header Authorization e renovado contra o auth server.
 * - `sanctum`: sessão que nasceu de um token Sanctum e nunca passou pelo auth
 *   server (login por link/autologin, convite, impersonação). O token em si
 *   NUNCA é guardado pelo JS: ele vive só no cookie httpOnly `token`, que o
 *   browser anexa sozinho (`withCredentials`). Por isso nada de Authorization
 *   nessas requests — o header teria precedência sobre o cookie no backend.
 *
 * O que fica salvo aqui é só este booleano (mais o vencimento, um número), o
 * suficiente para escolher a rota de renovação certa: o auth server para JWT,
 * `PUT /api/auth/refresh` para Sanctum.
 *
 * Vai em `localStorage`, não `sessionStorage`, porque a marca precisa ter o
 * MESMO escopo do cookie que ela descreve: o cookie é do browser inteiro, e uma
 * marca por aba faria uma segunda aba se achar em sessão JWT enquanto o cookie
 * de uma impersonação continua indo junto em toda request. Toda request que
 * sair sem Authorization (no boot o access token ainda não existe em memória,
 * e uma renovação que falha deixa a request sair mesmo assim) cairia no
 * fallback `bearerToken() ?? cookie('token')` do backend e autenticaria como a
 * sessão errada — no tenant errado. Os dois lados têm que concordar sobre quem
 * está logado.
 */
export type AuthMode = 'jwt' | 'sanctum'

const AUTH_MODE_STORAGE_KEY = 'pcare:auth:mode'
const SANCTUM_EXPIRES_AT_STORAGE_KEY = 'pcare:auth:sanctumExpiresAt'

function readStorage(key: string): string | null {
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

function writeStorage(key: string, value: string | null) {
    try {
        if (value === null) {
            window.localStorage.removeItem(key)
        } else {
            window.localStorage.setItem(key, value)
        }
    } catch {
        // localStorage indisponível (aba privada, etc.): a sessão perde a
        // sobrevivência a F5, mas segue funcionando via memória.
    }
}

let authMode: AuthMode =
    readStorage(AUTH_MODE_STORAGE_KEY) === 'sanctum' ? 'sanctum' : 'jwt'
let sanctumExpiresAt: number | null =
    Number(readStorage(SANCTUM_EXPIRES_AT_STORAGE_KEY)) || null

export function getAuthMode(): AuthMode {
    return authMode
}

export function isSanctumSession(): boolean {
    return authMode === 'sanctum'
}

/**
 * @param expiresInSeconds validade devolvida pelo backend, só para renovar
 * antes de vencer — o cookie em si é httpOnly e o JS nunca o lê.
 */
export function setSanctumSession(expiresInSeconds?: number) {
    authMode = 'sanctum'
    writeStorage(AUTH_MODE_STORAGE_KEY, 'sanctum')
    if (expiresInSeconds) {
        sanctumExpiresAt = Date.now() + expiresInSeconds * 1000
        writeStorage(SANCTUM_EXPIRES_AT_STORAGE_KEY, String(sanctumExpiresAt))
    }
}

export function clearSanctumSession() {
    authMode = 'jwt'
    sanctumExpiresAt = null
    writeStorage(AUTH_MODE_STORAGE_KEY, null)
    writeStorage(SANCTUM_EXPIRES_AT_STORAGE_KEY, null)
}

/**
 * Instância própria, sem os interceptors de `libs/axios` — a renovação não
 * pode passar pelo interceptor que, por sua vez, chama a renovação.
 */
const sanctumAxios = axios.create({ withCredentials: true })

type SanctumRefreshResponse = {
    data: { expires_in?: number }
}

/**
 * Renova a sessão Sanctum: manda o cookie, recebe um cookie novo com validade
 * esticada. `businessId` troca o business ativo da sessão na mesma chamada.
 */
export function refreshSanctumSession(
    businessId?: string | null
): Promise<void> {
    return sanctumAxios
        .put<SanctumRefreshResponse>(
            '/api/auth/refresh',
            businessId ? { business_id: businessId } : {}
        )
        .then((response) => {
            setSanctumSession(response.data?.data?.expires_in)
        })
}

function isSanctumSessionExpired(skewMs = 60_000): boolean {
    if (!sanctumExpiresAt) return false
    return Date.now() + skewMs >= sanctumExpiresAt
}

let sanctumRefreshInFlight: Promise<void> | null = null

/**
 * Equivalente Sanctum do `ensureFreshAccessToken`: renova a sessão antes de a
 * request sair, se o cookie estiver perto de vencer. Deduplicado.
 */
export async function ensureFreshSanctumSession(): Promise<void> {
    if (!isSanctumSession() || !isSanctumSessionExpired()) return

    if (!sanctumRefreshInFlight) {
        sanctumRefreshInFlight = refreshSanctumSession().finally(() => {
            sanctumRefreshInFlight = null
        })
    }
    await sanctumRefreshInFlight
}

/**
 * @param skewMs margem de segurança: trata o token como expirado um pouco
 * antes do vencimento real, pra nunca sair uma request com um token que
 * expira no meio do caminho até o servidor.
 */
export function isAccessTokenExpired(skewMs = 15_000): boolean {
    if (!tokenState.accessToken || !tokenState.expiresAt) return true
    return Date.now() + skewMs >= tokenState.expiresAt
}

export type AccessTokenClaims = {
    permissions?: string[]
    modules?: string[]
    business_id?: string
    [claim: string]: unknown
}

/**
 * Lê as claims do access_token JWT atual sem validar a assinatura — o token
 * já chegou validado pelo auth server, aqui só interessa ler o que ele já
 * carrega (`permissions`/`modules`) em vez de bater no Laravel de novo.
 * Retorna null se não houver token em memória (sessão Sanctum, por exemplo)
 * ou se o payload não puder ser decodificado.
 */
export function getAccessTokenClaims(): AccessTokenClaims | null {
    if (!tokenState.accessToken) return null
    const payload = tokenState.accessToken.split('.')[1]
    if (!payload) return null
    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
                .join('')
        )
        return JSON.parse(json)
    } catch {
        return null
    }
}

function base64url(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

function generateCodeVerifier(): string {
    return base64url(crypto.getRandomValues(new Uint8Array(32)))
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(verifier)
    )
    return base64url(new Uint8Array(digest))
}

export type MFAChallenge = {
    mfaRequired: true
    method: string
    ticket: string
    authRequest: string
}

export type LoginResult = { code: string; state: string } | MFAChallenge

type LoginAPIResponse = {
    code?: string
    state?: string
    mfa_required?: boolean
    method?: string
    ticket?: string
    auth_request?: string
}

function toLoginResult(data: LoginAPIResponse): LoginResult {
    if (data.mfa_required) {
        return {
            mfaRequired: true,
            method: data.method ?? 'totp',
            ticket: data.ticket ?? '',
            authRequest: data.auth_request ?? '',
        }
    }
    return { code: data.code ?? '', state: data.state ?? '' }
}

/**
 * Início do login: senha contra o auth server. Gera o par PKCE desta
 * tentativa — se o segundo fator for necessário, o `authRequest` devolvido
 * carrega tudo que a próxima chamada (loginWithTotp) precisa, sem repetir os
 * parâmetros PKCE.
 */
export async function loginWithPassword(
    email: string,
    password: string,
    clientId: string = CLIENT_ID,
    // "business" é o único scope que existe pro client `user` — nenhum dos
    // dois clients consome OIDC (sem id_token/userinfo), então não há
    // openid/profile/email a pedir. O client `admin` nunca tem business_id,
    // então não pede escopo nenhum (string vazia).
    scope: string = clientId === ADMIN_CLIENT_ID ? '' : 'business'
): Promise<LoginResult> {
    const verifier = generateCodeVerifier()
    const challenge = await generateCodeChallenge(verifier)
    pendingCodeVerifier = verifier

    const response = await authAxios.post<LoginAPIResponse>(
        '/api/login',
        {
            email,
            password,
            redirect_uri: REDIRECT_URI,
            scope,
            state: base64url(crypto.getRandomValues(new Uint8Array(16))),
            code_challenge: challenge,
            code_challenge_method: 'S256',
        },
        { params: { client_id: clientId } }
    )

    return toLoginResult(response.data)
}

/** Segundo fator (TOTP) — completa o login iniciado em loginWithPassword. */
export async function loginWithTotp(
    challenge: MFAChallenge,
    code: string,
    clientId: string = CLIENT_ID
): Promise<LoginResult> {
    const response = await authAxios.post<LoginAPIResponse>(
        '/api/login/mfa/totp',
        {
            ticket: challenge.ticket,
            code,
            auth_request: challenge.authRequest,
        },
        { params: { client_id: clientId } }
    )

    return toLoginResult(response.data)
}

type TokenResponse = {
    access_token: string
    token_type: string
    expires_in: number
    id_token?: string
}

/** Troca o `code` do login por um access_token — fecha o fluxo PKCE. */
export async function exchangeCode(
    code: string,
    clientId: string = CLIENT_ID
): Promise<void> {
    const verifier = pendingCodeVerifier
    pendingCodeVerifier = null
    if (!verifier) {
        throw new Error(
            'code_verifier ausente: o login precisa ser refeito desde o início'
        )
    }

    const response = await authAxios.post<TokenResponse>(
        '/token',
        new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            redirect_uri: REDIRECT_URI,
            code,
            code_verifier: verifier,
        })
    )

    setAccessToken(
        response.data.access_token,
        response.data.expires_in,
        clientId
    )
}

/**
 * Renova o access_token via o refresh_token no cookie httpOnly — o browser
 * anexa o cookie sozinho (withCredentials), o JS nunca vê o valor dele. Usa o
 * client_id da sessão que emitiu o token atual (guardado por exchangeCode).
 *
 * @param businessId Passar troca o business ativo da sessão sem exigir novo
 * login — o auth server reemite o access_token com o `business_id`/
 * `permissions`/`modules` novos direto nas claims (mesma chamada, sem round-trip
 * extra ao Laravel).
 */
export async function refreshAccessToken(
    clientId: string = tokenState.clientId ?? CLIENT_ID,
    businessId?: string | null
): Promise<void> {
    const params: Record<string, string> = {
        grant_type: 'refresh_token',
        client_id: clientId,
    }
    if (businessId) params.business_id = businessId

    const response = await authAxios.post<TokenResponse>(
        '/token',
        new URLSearchParams(params)
    )

    setAccessToken(
        response.data.access_token,
        response.data.expires_in,
        clientId
    )
}

/**
 * Revoga a sessão no auth server (o refresh_token inteiro, via cookie) e
 * limpa o access_token em memória — chamado no logout.
 */
export async function revokeSession(): Promise<void> {
    const clientId = tokenState.clientId ?? CLIENT_ID
    clearAccessToken()
    await authAxios
        .post('/revoke', new URLSearchParams({ client_id: clientId }))
        .catch(() => {
            // Logout local já aconteceu (token em memória limpo); uma falha
            // aqui só significa que a sessão no auth server expira sozinha.
        })
}

let refreshInFlight: Promise<void> | null = null

/**
 * Chamada antes de qualquer request autenticada: se o token já expirou (ou
 * está perto disso), renova ANTES de mandar a request, em vez de deixá-la
 * sair sabidamente destinada a um 401 pra só então reagir.
 *
 * Deduplicado: várias requests simultâneas veem o token expirado ao mesmo
 * tempo, mas só uma chamada de refresh de fato sai.
 */
export async function ensureFreshAccessToken(): Promise<void> {
    if (!tokenState.accessToken || !isAccessTokenExpired()) return

    if (!refreshInFlight) {
        refreshInFlight = refreshAccessToken().finally(() => {
            refreshInFlight = null
        })
    }
    await refreshInFlight
}
